import React, { useCallback, useMemo, useRef, useState, useEffect } from "react"
import useSWR from "swr"
import {
	Box,
	Button,
	Inline,
	Stack,
	Text,
	Icons,
	CheckboxInput,
	Input,
	Grid,
	Col,
	Tooltip,
	Spinner,
	Time,
	Component,
	Tabs,
	TabsList,
	TabItem,
	TabContent,
	HorizontalScrollShadow,
} from "@sembark-travel/ui/base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui/dialog"
import {
	Form,
	TextInputField,
	TextAreaInputField,
	validateFormValues,
	withServerErrors,
	SubmissionError,
} from "@sembark-travel/ui/form"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { useMounted } from "@sembark-travel/ui/base"
import * as Validator from "yup"
import { showSnackbar } from "@sembark-travel/ui/snackbar"

interface ApiError {
	response?: {
		data?: {
			errors?: Array<{ message?: string }> | Record<string, string[]>
		}
	}
	message?: string
}

type TColumn = {
	id: number
	name: string
	slug: string
	custom_column_name?: string | null
	description?: string | null
	selected?: boolean | "1" | "0" | 0 | 1
	group: {
		id: number
		name: string
	}
}
type TPreset = {
	id: number
	name: string
	tenant_id?: number | null
	description?: string | null
	created_at: string
	created_by: {
		id: number
		uid: string
		name: string
		email: string
	}
}

interface ReportTypeHeadingsResponse {
	id?: number
	headings: Array<TColumn>
	presets?: Array<TPreset>
}

interface PresetResponse {
	id: number
	name: string
	description: string
	tenant_id?: number | null
	headings: Array<TColumn>
}

function ReportTypeHeadingsXHR(xhr: XHRInstance) {
	return {
		async getHeadings(reportType: string): Promise<ReportTypeHeadingsResponse> {
			return xhr
				.get(`/report-type-headings?report_type=${reportType}`)
				.then((response) => response.data.data)
		},
		async getPreset(presetId: number): Promise<PresetResponse> {
			return xhr
				.get(`/report-type-headings/${presetId}`)
				.then((response) => response.data.data)
		},
	}
}

export interface ColumnExportProps {
	title?: string
	reportType: string
	onCancel?: () => void
	downloadUrl?: string
	requestParams?: Record<string, unknown>
	timezoneOffset?: number
	modalType: "customColumn" | "presets"
	setModalType?: (modalType: "customColumn" | "presets") => void
}

function useReportTypeHeadings(reportType: string) {
	const xhr = useXHR()
	const mounted = useMounted()
	const {
		data,
		error,
		mutate: revalidate,
		isValidating,
		isLoading,
	} = useSWR(
		reportType ? `/report-type-headings/${reportType}` : null,
		() =>
			reportType
				? ReportTypeHeadingsXHR(xhr).getHeadings(reportType)
				: Promise.reject("Report type is required"),
		{
			onError: (error) => {
				if (mounted()) {
					showSnackbar(error?.message || "Failed to fetch report type headings")
				}
			},
			onSuccess: (data) => {
				if (!data.headings || data.headings.length === 0) {
					showSnackbar("No columns found for this report type.")
				}
			},
		}
	)

	return {
		data,
		error,
		revalidate,
		isLoading,
		isValidating,
	}
}

function usePreset(presetId: number | null) {
	const xhr = useXHR()
	const mounted = useMounted()
	const {
		data,
		error,
		mutate: revalidate,
		isValidating,
		isLoading,
	} = useSWR(
		presetId ? `/report-type-headings/preset/${presetId}` : null,
		() =>
			presetId
				? ReportTypeHeadingsXHR(xhr).getPreset(presetId)
				: Promise.reject("Preset ID is required"),
		{
			onError: (error) => {
				if (mounted()) {
					showSnackbar(error?.message || "Failed to fetch preset data")
				}
			},
		}
	)

	return {
		data,
		error,
		revalidate,
		isLoading,
		isValidating,
	}
}

export function ColumnExport({
	reportType,
	onCancel,
	downloadUrl,
	requestParams = {},
	timezoneOffset,
	modalType,
	setModalType,
}: ColumnExportProps) {
	// Ref to track the index of the column being dragged during reordering
	const dragIndexRef = useRef<number | null>(null)
	const xhr = useXHR()
	const mounted = useMounted()
	// State to track where the dragged item will be dropped (for visual indicator)
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
	const [originalColumns, setOriginalColumns] = useState<Array<TColumn>>([])
	const [filteredColumns, setFilteredColumns] = useState<Array<TColumn>>([])
	const [presets, setPresets] = useState<Array<TPreset>>([])
	const [selected, setSelected] = useState<Array<TColumn>>([])
	const [activePresetId, setActivePresetId] = useState<number | null>(null)
	const [isEditingPreset, setIsEditingPreset] = useState(false)
	const [presetUpdating, setPresetUpdating] = useState(false)

	// Stores baseline column IDs of active preset to detect changes in current selection
	const [activePresetBaseline, setActivePresetBaseline] = useState<
		Array<number>
	>([])
	const [reportTypeId, setReportTypeId] = useState<number | null>(null)
	// Download states
	const [storageId, setStorageId] = useState<string | undefined>()
	const [isGenerating, setIsGenerating] = useState(false)
	const [isSaveDialogOpen, openSaveDialog, closeSaveDialog] = useDialog()

	const isAllSelected = useMemo(() => {
		if (filteredColumns.length === 0) return false
		const selectedIds = new Set(selected.map((col) => col.id))
		return filteredColumns.every((col) => selectedIds.has(col.id))
	}, [filteredColumns, selected])

	const hasPresetChanges = useMemo(() => {
		if (!activePresetId) return false
		const current = selected.map((c) => c.id)
		// Check if number of columns changed
		if (current.length !== activePresetBaseline.length) return true
		// Check if order of columns changed
		for (let i = 0; i < current.length; i++) {
			if (current[i] !== activePresetBaseline[i]) return true
		}
		return false
	}, [activePresetId, selected, activePresetBaseline])
	const selectedIds = useMemo(
		() => new Set(selected.map((s) => s.id)),
		[selected]
	)

	// Helper function to organize columns by groups
	const organizeColumnsByGroups = useMemo(() => {
		const tempGroupedColumns: { [key: string]: TColumn[] } = {}
		const othersGroupColumns: TColumn[] = []
		const isSearchResult = filteredColumns.length !== originalColumns.length

		// First pass: group all columns by their group (id-name combination)
		filteredColumns.forEach((col) => {
			const groupKey = `${col.group.id}-${col.group.name}`
			if (!tempGroupedColumns[groupKey]) {
				tempGroupedColumns[groupKey] = []
			}
			tempGroupedColumns[groupKey].push(col)
		})

		// Second pass: identify single-column groups and merge them into "Others"
		const finalGroupedColumns: {
			[key: string]: { name: string; columns: TColumn[] }
		} = {}

		Object.entries(tempGroupedColumns).forEach(([groupKey, columns]) => {
			// Check if this is a search result (filteredColumns is different from originalColumns)
			if (columns.length === 1 && !isSearchResult) {
				// Single column group - add to "Others" only when not searching
				othersGroupColumns.push(...columns)
			} else {
				// Multi-column group or single column during search - keep separate
				const groupName = columns[0].group.name
				finalGroupedColumns[groupKey] = { name: groupName, columns }
			}
		})

		// Add "Others" group if there are single-column groups (only when not searching)
		if (othersGroupColumns.length > 0 && !isSearchResult) {
			finalGroupedColumns["Others"] = {
				name: "Others",
				columns: othersGroupColumns,
			}
		}

		return finalGroupedColumns
	}, [filteredColumns, originalColumns.length])

	//-------SWR hook to fetch columns---------//
	const {
		data: reportHeadingsData,
		revalidate: revalidateReportHeadings,
		isLoading: isLoadingColumns,
	} = useReportTypeHeadings(reportType)

	//-------SWR hook to fetch preset data---------//
	const {
		data: presetData,
		error: presetError,
		isLoading: isLoadingPresets,
		revalidate: revalidatePreset,
	} = usePreset(activePresetId)

	// Update state when data changes
	useEffect(() => {
		if (!mounted() || !reportHeadingsData) return

		const { headings, presets, id } = reportHeadingsData

		if (headings.length > 0) {
			const columns = (headings as Array<TColumn>).map((col: TColumn) => ({
				id: Number(col.id),
				name: col.name,
				slug: col.slug,
				description: col.description ?? null,
				group: col.group,
				selected: col.selected ?? false,
			}))
			setOriginalColumns(columns)
			setFilteredColumns(columns)

			// Set initial selected columns based on the selected property from API
			const initiallySelectedColumns = columns.filter((col) => col.selected)
			if (initiallySelectedColumns.length > 0 && modalType === "customColumn") {
				setSelected(initiallySelectedColumns)
			}
		} else {
			setOriginalColumns([])
			setFilteredColumns([])
		}

		if (presets) {
			setPresets(presets as Array<TPreset>)
		}

		if (id) {
			setReportTypeId(Number(id))
		}
	}, [reportHeadingsData, modalType, mounted])

	// Update selection when preset data changes
	useEffect(() => {
		if (!mounted() || !presetData || !originalColumns.length) return

		const { headings } = presetData
		if (Array.isArray(headings) && headings.length > 0) {
			// Map headings to actual column objects and filter out any missing ones
			const matchingColumns = headings
				.map((h) => {
					const base = originalColumns.find((c) => c.id === Number(h.id))
					return base ? { ...base } : null
				})
				.filter(Boolean) as Array<TColumn>
			setSelected(matchingColumns)
			setActivePresetBaseline(matchingColumns.map((c) => c.id))
		} else {
			// If no columns found, reset selection
			setSelected([])
			setActivePresetBaseline([])
		}
	}, [presetData, originalColumns, mounted])

	// Handle preset loading errors
	useEffect(() => {
		if (presetError && activePresetId) {
			setActivePresetId(null)
			setSelected([])
			setActivePresetBaseline([])
		}
	}, [presetError, activePresetId])

	// Clear selection and edit mode when switching to Custom Columns from Presets
	useEffect(() => {
		if (modalType === "customColumn" && (activePresetId || isEditingPreset)) {
			setSelected([])
			setActivePresetId(null)
			setActivePresetBaseline([])
			setIsEditingPreset(false)
		}
	}, [modalType, activePresetId, isEditingPreset])

	// Handle tab switching with confirmation when needed
	const handleTabSwitch = useCallback(
		async (
			targetMode: "presets" | "customColumn",
			confirm: () => Promise<boolean>
		) => {
			if (
				targetMode === "presets" &&
				modalType === "customColumn" &&
				selected.length > 0
			) {
				const confirmed = await confirm()

				if (!confirmed) {
					return
				}

				setSelected([])
				setActivePresetId(null)
				setActivePresetBaseline([])
				setIsEditingPreset(false)
			}

			setModalType?.(targetMode)
		},
		[modalType, selected.length, setModalType]
	)

	const toggleSelection = useCallback((col: TColumn, checked: boolean) => {
		setSelected((prev) => {
			if (checked) {
				if (prev.find((p) => p.id === col.id)) return prev
				return prev.concat(col)
			}
			return prev.filter((p) => p.id !== col.id)
		})
	}, [])

	const onToggleAll = useCallback(
		(checked: boolean) => {
			setSelected((prev) => {
				if (checked) {
					// Add all columns that are not in the selected
					const prevIds = new Set(prev.map((col) => col.id))
					const newColumns = filteredColumns.filter(
						(col) => !prevIds.has(col.id)
					)
					return [...prev, ...newColumns]
				} else {
					// Remove columns that are not in the filteredColumns
					return prev.filter(
						(col) => !filteredColumns.find((c) => c.id === col.id)
					)
				}
			})
		},
		[filteredColumns]
	)

	// Helper function to toggle all columns in a group
	const toggleGroupSelection = useCallback(
		(groupColumns: TColumn[], checked: boolean) => {
			setSelected((prev) => {
				if (checked) {
					// Add all group columns that aren't already selected
					const newColumns = groupColumns.filter(
						(col) => !prev.find((p) => p.id === col.id)
					)
					return prev.concat(newColumns)
				} else {
					// Remove all group columns
					return prev.filter((p) => !groupColumns.find((gc) => gc.id === p.id))
				}
			})
		},
		[]
	)

	const onDragStart = (index: number) => () => {
		dragIndexRef.current = index
		setDragOverIndex(null) // Clear any previous drag over indicator
	}

	// Handle drag over to show visual indicator
	const onDragOverWithIndex = (index: number) => (e: React.DragEvent) => {
		e.preventDefault()
		if (dragIndexRef.current !== null && dragIndexRef.current !== index) {
			setDragOverIndex(index)
		}
	}

	// Handle drag leave to clear visual indicator
	const onDragLeave = () => {
		setDragOverIndex(null)
	}

	// Moves the dragged column to the drop position and updates indices
	const onDrop = (dropIndex: number) => (e: React.DragEvent) => {
		e.preventDefault()
		const from = dragIndexRef.current
		if (from === null || from === dropIndex) return
		setSelected((prev) => {
			const next = prev.slice()
			const [moved] = next.splice(from, 1)
			next.splice(dropIndex, 0, moved)
			return next
		})
		dragIndexRef.current = null
		setDragOverIndex(null) // Clear drag over indicator
	}

	// Select the clicked preset - SWR will automatically fetch the data
	const handlePresetClick = useCallback(
		(presetId: number) => {
			if (activePresetId === presetId) return

			setActivePresetId(presetId)
		},
		[activePresetId]
	)

	//search for a column when user types in the search input
	const handleSearch = useCallback(
		(query: string) => {
			if (!query.trim()) {
				setFilteredColumns(originalColumns)
			} else {
				setFilteredColumns(
					originalColumns.filter((col) =>
						col.name.toLowerCase().includes(query.toLowerCase())
					)
				)
			}
			dragIndexRef.current = null
		},
		[originalColumns]
	)

	//fetch create preset api when save button is clicked
	const createPreset = useCallback(
		async (name: string, description: string) => {
			if (!reportTypeId) {
				showSnackbar("Report Type not loaded. Please try again.")
				return
			}
			const payload = {
				name,
				description,
				report_type_id: reportTypeId,
				headings: selected.map((s) => ({ id: s.id })),
			}
			try {
				await xhr.post("/report-type-headings", payload)
				showSnackbar("Preset created successfully.")
				setSelected([])
				setActivePresetId(null)
				closeSaveDialog()
				setModalType?.("presets")
				/* Revalidate headings data to refresh presets list */
				await revalidateReportHeadings()
			} catch (error: unknown) {
				const apiError = error as ApiError
				if (
					apiError?.response?.data?.errors &&
					typeof apiError.response.data.errors === "object"
				) {
					Object.entries(apiError.response.data.errors).forEach(
						([field, messages]) => {
							if (Array.isArray(messages)) {
								messages.forEach((msg) =>
									showSnackbar(msg || "An error occurred.")
								)
							}
						}
					)
				} else if (Array.isArray(apiError?.response?.data?.errors)) {
					apiError.response.data.errors.forEach((e: { message?: string }) => {
						showSnackbar(e?.message || "An error occurred.")
					})
				} else {
					showSnackbar(
						apiError?.message || "Failed to create preset. Please try again."
					)
				}
			}
		},
		[
			reportTypeId,
			selected,
			xhr,
			closeSaveDialog,
			setModalType,
			revalidateReportHeadings,
		]
	)

	//fetch update preset api when update is clicked for a preset
	const handleUpdatePreset = useCallback(async () => {
		if (!activePresetId) return
		setPresetUpdating(true)
		try {
			if (presetUpdating) return
			await xhr.put(`/report-type-headings/${activePresetId}`, {
				report_type_id: reportTypeId,
				headings: selected.map((s) => ({ id: s.id })),
			})
			showSnackbar("Preset updated successfully.")
			setActivePresetBaseline(selected.map((s) => s.id))

			await revalidatePreset()
		} catch (error) {
			showSnackbar("Failed to update preset. Please try again.")
		} finally {
			setPresetUpdating(false)
		}
	}, [
		activePresetId,
		selected,
		xhr,
		reportTypeId,
		presetUpdating,
		revalidatePreset,
	])

	//fetch delete preset api when delete is clicked for a preset
	const handleDeletePreset = useCallback(
		async (presetId: number) => {
			try {
				await xhr.delete(`/report-type-headings/${presetId}`)
				// Remove preset from local state
				setPresets((prev) => prev.filter((p) => p.id !== presetId))

				//Reset selection and active preset id
				setActivePresetId(null)
				setSelected([])
				setActivePresetBaseline([])
				setIsEditingPreset(false)
				showSnackbar("Preset deleted successfully.")
			} catch (error: unknown) {
				const e = error as Error
				showSnackbar(e.message || "Failed to delete preset. Please try again.")
			}
		},
		[xhr]
	)

	//start the download process when download button is clicked
	const handleDownload = useCallback(async () => {
		if (!downloadUrl || selected.length === 0 || isGenerating) return

		setIsGenerating(true)
		try {
			const payload = {
				...requestParams,
				timezone_offset: timezoneOffset,
				headings: selected.map((s) => ({ slug: s.slug })),
			}
			const response = await xhr.get(downloadUrl, { params: payload })

			if (response.data?.data?.id) {
				setStorageId(response.data.data.id)
			} else {
				throw new Error("No storage ID received")
			}
		} catch (error: unknown) {
			const apiError = error as ApiError
			if (
				apiError?.response?.data?.errors &&
				typeof apiError.response.data.errors === "object"
			) {
				Object.entries(apiError.response.data.errors).forEach(
					([field, messages]) => {
						if (Array.isArray(messages)) {
							messages.forEach((msg) =>
								showSnackbar(msg || "An error occurred.")
							)
						}
					}
				)
			} else if (Array.isArray(apiError?.response?.data?.errors)) {
				apiError.response.data.errors.forEach((e: { message?: string }) => {
					showSnackbar(e?.message || "An error occurred.")
				})
			} else {
				showSnackbar(
					apiError?.message || "Failed to start download. Please try again."
				)
			}
			setIsGenerating(false)
		}
	}, [downloadUrl, selected, requestParams, timezoneOffset, xhr, isGenerating])

	//Runs when we get the file url
	const handleFileDownload = useCallback(
		(url: string) => {
			if (!url) return
			setIsGenerating(true)
			const link = document.createElement("a")
			link.href = url
			link.download = `${reportType}_report_${new Date().toISOString().split("T")[0]}.xlsx`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			// Reset download state after file is downloaded
			setTimeout(() => {
				setStorageId(undefined)
				setIsGenerating(false)
			}, 500)
		},
		[reportType]
	)

	//check for the file url every 2 seconds
	useEffect(() => {
		if (!storageId) return

		let pollCount = 0
		const maxPolls = 60 // 1 minutes timeout

		const interval = setInterval(async () => {
			pollCount++

			if (pollCount > maxPolls) {
				showSnackbar("Download timed out. Please try again.")
				setIsGenerating(false)
				clearInterval(interval)
				return
			}

			try {
				const response = await xhr.get(`temporary-storage/${storageId}`)
				const data = response.data.data

				if (data.url && mounted()) {
					// File is ready, stop polling and download
					setStorageId(undefined)
					setIsGenerating(false)
					clearInterval(interval)
					handleFileDownload(data.url)
				} else if (data.status === "failed" && mounted()) {
					showSnackbar("Download failed. Please try again.")
					setIsGenerating(false)
					clearInterval(interval)
				}
			} catch (error) {
				showSnackbar("Error checking download status. Please try again.")
				clearInterval(interval)
				setIsGenerating(false)
			}
			//check every 2 seconds
		}, 2000)

		return () => clearInterval(interval)
	}, [storageId, xhr, mounted, handleFileDownload])

	return (
		<Stack>
			<Tabs layout="col" gap="2">
				<Stack gap="3">
					<ConfirmationDialogProvider>
						{({ confirm }) => (
							<>
								<TabsList>
									<TabItem
										active={modalType === "presets"}
										onClick={() => handleTabSwitch("presets", confirm)}
										disabled={!setModalType}
									>
										<Icons.HorizontalSliders /> Presets
									</TabItem>
									<TabItem
										active={modalType === "customColumn"}
										onClick={() => handleTabSwitch("customColumn", confirm)}
										disabled={!setModalType}
									>
										<Icons.Columns /> Custom Columns
									</TabItem>
								</TabsList>

								<ConfirmationDialog
									title="Switching to Presets?"
									destructiveAction
								>
									<Stack gap="4">
										<Stack gap="2">
											<Text color="warning" fontWeight="semibold">
												<Icons.Attention /> You have {selected.length} column
												{selected.length !== 1 ? "s" : ""} selected
											</Text>
											<Text fontSize="sm" color="muted">
												Switching to Presets will clear your current custom
												column selection.
											</Text>
										</Stack>
										<Text>Are you sure you want to switch to Presets?</Text>
									</Stack>
								</ConfirmationDialog>
							</>
						)}
					</ConfirmationDialogProvider>

					<Inline justifyContent="end">
						<Button
							level="primary"
							size="sm"
							onClick={handleDownload}
							disabled={!selected.length || isGenerating}
							aria-busy={isGenerating}
						>
							<Icons.DocumentDownload
								animation={isGenerating ? "pulse" : undefined}
							/>
							{isGenerating ? "Downloading..." : "Download Report"}
						</Button>
					</Inline>
				</Stack>

				<TabContent>
					<Stack gap="8">
						{modalType === "presets" ? (
							<PresetsSection
								presets={presets}
								isLoadingColumns={isLoadingColumns}
								activePresetId={activePresetId}
								handlePresetClick={handlePresetClick}
								isEditingPreset={isEditingPreset}
								setIsEditingPreset={setIsEditingPreset}
								handleDeletePreset={handleDeletePreset}
							/>
						) : null}
						{modalType === "customColumn" || isEditingPreset ? (
							<ColumnsSection
								isAllSelected={isAllSelected}
								onToggleAll={onToggleAll}
								handleSearch={handleSearch}
								organizeColumnsByGroups={organizeColumnsByGroups}
								selectedIds={selectedIds}
								toggleGroupSelection={toggleGroupSelection}
								toggleSelection={toggleSelection}
								isLoadingColumns={isLoadingColumns}
							/>
						) : null}
						<Stack>
							<Box
								position="relative"
								padding="3"
								paddingTop="6"
								borderRadius="md"
								borderWidth="1"
								borderColor="muted"
								backgroundColor="subtle"
							>
								<Inline
									position="absolute"
									borderRadius="lg"
									borderWidth="1"
									borderColor="muted"
									backgroundColor="primary"
									paddingX="2"
									paddingY="1"
									style={{
										top: "-15px",
									}}
								>
									<Text fontWeight="semibold">
										{`Selected Columns (${selected?.length})`}
									</Text>
								</Inline>
								{isLoadingPresets ? (
									<Stack alignItems="center" paddingY="4">
										<Spinner />
									</Stack>
								) : selected.length === 0 ? (
									<Stack alignItems="center" paddingBottom="2">
										<Icons.Info size="6" color="muted" />
										<Text color="muted" fontWeight="semibold">
											No Selected Columns
										</Text>
									</Stack>
								) : (
									<Box
										style={{
											maxHeight: "300px",
											overflowY: "auto",
										}}
									>
										<Inline gap="3" flexWrap="wrap">
											{selected.map((s, index) => {
												const isReorderDisabled =
													modalType === "presets" && !isEditingPreset
												const dragIndex = dragIndexRef.current as number
												const showDropIndicator =
													dragOverIndex === index &&
													dragIndex !== null &&
													dragIndex !== index

												return (
													<React.Fragment key={s.id}>
														{showDropIndicator && dragIndex > index && (
															<Box
																height="full"
																borderRadius="none"
																marginY="auto"
																style={{
																	width: "2px",
																	minHeight: "40px",
																	backgroundColor: "#3e63dd",
																}}
															/>
														)}

														<Box
															draggable={!isReorderDisabled}
															onDragStart={
																isReorderDisabled
																	? undefined
																	: onDragStart(index)
															}
															onDragOver={
																isReorderDisabled
																	? undefined
																	: onDragOverWithIndex(index)
															}
															onDragLeave={
																isReorderDisabled ? undefined : onDragLeave
															}
															onDrop={
																isReorderDisabled ? undefined : onDrop(index)
															}
															paddingX="1"
															paddingY="1"
															paddingRight={isReorderDisabled ? "2" : "1"}
															borderWidth="1"
															rounded="md"
															backgroundColor="default"
															style={{
																cursor: isReorderDisabled ? "default" : "grab",
															}}
														>
															<Inline gap="8" alignItems="center">
																<Inline gap="2" alignItems="center">
																	<Box
																		paddingX="2"
																		paddingY="1"
																		bgColor="inset"
																		rounded="md"
																	>
																		{index + 1}
																	</Box>
																	<Text>{s.name}</Text>
																</Inline>
																{!isReorderDisabled && (
																	<Inline
																		alignItems="center"
																		bgColor="primary"
																		rounded="md"
																		paddingX="2"
																		paddingY="1"
																		style={{
																			height: 26,
																			width: 26,
																		}}
																	>
																		<Icons.ArrowRightLeft
																			size="3"
																			color="primary"
																		/>
																	</Inline>
																)}
															</Inline>
														</Box>
														{showDropIndicator && dragIndex < index && (
															<Box
																height="full"
																borderRadius="none"
																marginY="auto"
																style={{
																	width: "2px",
																	minHeight: "40px",
																	backgroundColor: "#3e63dd",
																}}
															/>
														)}
													</React.Fragment>
												)
											})}
										</Inline>
									</Box>
								)}
							</Box>
							{selected?.length > 0 &&
								(modalType === "customColumn" ||
									(modalType === "presets" && isEditingPreset)) && (
									<Text color="muted" fontSize="sm">
										Note: Drag and drop to re-arrange columns
									</Text>
								)}
						</Stack>
						<Inline gap="3" justifyContent="end" flexWrap="wrap">
							{(modalType === "customColumn" || isEditingPreset) && (
								<Button
									onClick={openSaveDialog}
									status="primary"
									disabled={!selected.length}
								>
									<Icons.Ok />
									{activePresetId && isEditingPreset ? "Save as New" : "Save"}
								</Button>
							)}
							<SavePresetDialog
								open={isSaveDialogOpen}
								onClose={closeSaveDialog}
								selectedCount={selected.length}
								onSave={createPreset}
							/>
							{/* Update button - only shown when preset has changes and user has modified the selection */}
							{activePresetId && isEditingPreset && hasPresetChanges && (
								<Button
									status="primary"
									onClick={handleUpdatePreset}
									disabled={selected?.length === 0 || presetUpdating}
								>
									<Icons.Ok animation={presetUpdating ? "pulse" : undefined} />
									{presetUpdating ? "Updating..." : "Update"}
								</Button>
							)}
						</Inline>
					</Stack>
				</TabContent>
			</Tabs>
		</Stack>
	)
}

type PresetSectionProps = {
	presets: Array<TPreset>
	isLoadingColumns: boolean
	activePresetId: number | null
	handlePresetClick: (presetId: number) => void
	isEditingPreset: boolean
	setIsEditingPreset: (isEditingPreset: boolean) => void
	handleDeletePreset: (presetId: number) => void
}

function PresetsSection({
	presets,
	isLoadingColumns,
	activePresetId,
	handlePresetClick,
	isEditingPreset,
	setIsEditingPreset,
	handleDeletePreset,
}: PresetSectionProps) {
	return (
		<Box
			position="relative"
			padding="3"
			paddingTop="6"
			borderRadius="md"
			borderWidth="1"
			borderColor="muted"
		>
			<Inline
				position="absolute"
				borderRadius="lg"
				borderWidth="1"
				borderColor="muted"
				backgroundColor="primary"
				paddingX="2"
				paddingY="1"
				style={{ top: "-15px" }}
			>
				<Text fontWeight="semibold">{`Saved Presets (${presets.length})`}</Text>
			</Inline>

			{isLoadingColumns ? (
				<Stack alignItems="center" paddingY="4">
					<Spinner />
				</Stack>
			) : presets.length === 0 ? (
				<Stack alignItems="center" paddingBottom="2">
					<Icons.Info size="6" color="muted" />
					<Text color="muted" fontWeight="semibold">
						No presets available
					</Text>
				</Stack>
			) : (
				<HorizontalScrollShadow>
					<Inline gap="4" flexWrap="nowrap">
						{presets.map((p) => {
							const isActive = activePresetId === p.id
							return (
								<Box
									key={p.id}
									paddingY="2"
									paddingX={isActive ? "2" : "3"}
									rounded="md"
									borderWidth="1"
									minWidth="max"
									role="button"
									cursor="pointer"
									onClick={() => {
										handlePresetClick(p.id)
										if (activePresetId !== p.id) {
											setIsEditingPreset(false)
										}
									}}
									{...(isActive
										? { borderColor: "primary", backgroundColor: "primary" }
										: {
												borderColor: "default",
												backgroundColor: "default",
											})}
								>
									<Inline alignItems="center" gap="6">
										<Stack style={{ flex: 1 }} gap="1">
											{(() => {
												const nameNeedsTruncation = p.name.length > 50
												const descriptionNeedsTruncation =
													p.description && p.description.length > 50

												return (
													<Stack>
														{nameNeedsTruncation ? (
															<Tooltip content={p.name} placement="top-start">
																<Text
																	fontWeight="semibold"
																	textOverflow="truncate"
																	style={{
																		maxWidth: "200px",
																	}}
																>
																	{`${p.name.substring(0, 50)}...`}
																</Text>
															</Tooltip>
														) : (
															<Text fontWeight="semibold">{p.name}</Text>
														)}
														{descriptionNeedsTruncation ? (
															<Tooltip
																content={p.description || ""}
																placement="top-start"
															>
																<Text
																	fontSize="xs"
																	color="muted"
																	textOverflow="truncate"
																	style={{ maxWidth: "200px" }}
																>
																	{`${(p.description || "").substring(0, 50)}...`}
																</Text>
															</Tooltip>
														) : (
															<Text fontSize="xs" color="muted">
																{p.description}
															</Text>
														)}
													</Stack>
												)
											})()}
											<Text fontSize="xs" color="muted">
												Created on <Time timestamp={p.created_at} />
												<br />
												{p.created_by?.name ? `by ${p.created_by?.name}` : null}
											</Text>
										</Stack>
										{isActive && p.tenant_id !== null ? (
											<Inline gap="px">
												<Button
													level="tertiary"
													size="sm"
													onClick={() => {
														setIsEditingPreset(!isEditingPreset)
														handlePresetClick(p.id)
													}}
												>
													<Icons.Pencil size="4" />
												</Button>
												<ConfirmationDialogProvider>
													{({ confirm }) => (
														<>
															<Component initialState={false}>
																{({ state, setState }) => (
																	<Button
																		status="danger"
																		level="tertiary"
																		size="sm"
																		disabled={state}
																		onClick={() => {
																			confirm().then(async (confirmed) => {
																				if (confirmed) {
																					setState(true)
																					try {
																						await handleDeletePreset(p.id)
																					} finally {
																						setState(false)
																					}
																				}
																			})
																		}}
																	>
																		<Icons.Trash size="4" />
																	</Button>
																)}
															</Component>
															<ConfirmationDialog
																title="Delete Preset"
																destructiveAction
															>
																<Stack gap="4">
																	<Stack gap="2">
																		<Text color="danger" fontWeight="semibold">
																			<Icons.Attention /> Once deleted, Preset
																			cannot be restored.
																		</Text>
																		<Text fontSize="sm" color="muted">
																			You will be able to add a new one.
																		</Text>
																	</Stack>
																	<Text>
																		Are you sure you want to delete "{p.name}"
																		Preset?
																	</Text>
																</Stack>
															</ConfirmationDialog>
														</>
													)}
												</ConfirmationDialogProvider>
											</Inline>
										) : null}
									</Inline>
								</Box>
							)
						})}
					</Inline>
				</HorizontalScrollShadow>
			)}
		</Box>
	)
}

type ColumnsSectionProps = {
	isAllSelected: boolean
	onToggleAll: (isAllSelected: boolean) => void
	handleSearch: (search: string) => void
	toggleSelection: (col: TColumn, isSelected: boolean) => void
	selectedIds: Set<number>
	isLoadingColumns: boolean
	toggleGroupSelection: (
		groupColumns: Array<TColumn>,
		isGroupSelected: boolean
	) => void
	organizeColumnsByGroups: Record<string, { name: string; columns: TColumn[] }>
}
function ColumnsSection({
	isAllSelected,
	onToggleAll,
	handleSearch,
	toggleGroupSelection,
	selectedIds,
	toggleSelection,
	isLoadingColumns,
	organizeColumnsByGroups,
}: ColumnsSectionProps) {
	return (
		<Box
			position="relative"
			padding="3"
			borderRadius="md"
			borderWidth="1"
			borderColor="muted"
		>
			<Inline
				position="absolute"
				borderRadius="lg"
				borderWidth="1"
				borderColor="muted"
				backgroundColor="primary"
				paddingX="2"
				paddingY="1"
				style={{ top: "-15px" }}
			>
				<Text fontWeight="semibold">Columns</Text>
			</Inline>
			<Stack gap="3">
				<Grid alignItems="center" justifyContent="end" noGutters gap="6">
					<Inline gap="2" as="label">
						<CheckboxInput
							checked={isAllSelected}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								onToggleAll(e.target.checked)
							}
						/>
						<Text>Select All</Text>
					</Inline>
					<Col xs={12} sm={6} md={4} paddingX="3">
						<Input
							type="search"
							placeholder="Search by column name..."
							onChange={(e) => handleSearch(e.currentTarget.value)}
						/>
					</Col>
				</Grid>
				<HorizontalScrollShadow>
					<Inline gap="4" alignItems="start" position="relative">
						{isLoadingColumns ? (
							<Stack alignItems="center" paddingY="4" width="full">
								<Spinner />
							</Stack>
						) : (
							Object.entries(organizeColumnsByGroups).map(
								([groupKey, groupData]) => (
									<Box
										key={groupKey}
										borderWidth="1"
										padding="4"
										rounded="md"
										overflow="auto"
										style={{
											maxHeight: "380px",
											minWidth: "480px",
										}}
									>
										<Stack gap="3">
											{/* Group header */}
											<Inline
												as="label"
												gap="2"
												alignItems="center"
												whiteSpace="nowrap"
												cursor="pointer"
											>
												<CheckboxInput
													checked={groupData.columns.every((col) =>
														selectedIds.has(col.id)
													)}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
														toggleGroupSelection(
															groupData.columns,
															e.target.checked
														)
													}
												/>
												<Text fontWeight="semibold" textDecoration="underline">
													{groupData.name}
												</Text>
											</Inline>

											{/* Render all columns in this group */}
											{groupData.columns.map((col: TColumn) => {
												return (
													<Inline
														as="label"
														key={col.id}
														gap="2"
														alignItems="center"
														whiteSpace="nowrap"
														cursor="pointer"
														paddingLeft="6"
													>
														<CheckboxInput
															checked={selectedIds.has(col.id)}
															onChange={(
																e: React.ChangeEvent<HTMLInputElement>
															) => toggleSelection(col, e.target.checked)}
														/>
														<Text fontWeight="semibold">{col.name}</Text>
														{col.description && (
															<Text
																fontWeight="normal"
																color="muted"
															>{` (e.g: ${col.description})`}</Text>
														)}
													</Inline>
												)
											})}
										</Stack>
									</Box>
								)
							)
						)}
					</Inline>
				</HorizontalScrollShadow>
			</Stack>
		</Box>
	)
}

export default ColumnExport

export function ColumnExportInDialog({
	children,
	...props
}: Omit<ColumnExportProps, "onCancel" | "modalType"> & {
	children: (props: {
		open: (modalType: "customColumn" | "presets") => void
	}) => React.ReactNode
}) {
	const [isOpen, openDialog, close] = useDialog()
	const [modalType, setModalType] = useState<"customColumn" | "presets">(
		"presets"
	)

	const open = (type: "customColumn" | "presets") => {
		setModalType(type)
		openDialog()
	}

	return (
		<>
			{children({ open })}
			<Dialog
				open={isOpen}
				onClose={close}
				title={props.title || "Export Columns"}
				lg
			>
				<Dialog.Body>
					<ColumnExport
						{...props}
						modalType={modalType}
						setModalType={setModalType}
						onCancel={close}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type SavePresetDialogProps = {
	open: boolean
	onClose: () => void
	selectedCount: number
	onSave: (name: string, description: string) => Promise<void>
}

function SavePresetDialog({
	open,
	onClose,
	selectedCount,
	onSave,
}: SavePresetDialogProps) {
	const initialValues = { name: "", description: "" }

	const validationSchema = Validator.object().shape({
		name: Validator.string()
			.trim()
			.max(100, "Please use 100 or less characters")
			.min(3, "Please use atleast 3 characters")
			.required("Please provide the name for the preset"),
		description: Validator.string()
			.trim()
			.max(150, "Please use 150 or less characters")
			.min(3, "Please use atleast 3 characters")
			.required("Please provide the description for the preset"),
	})

	return (
		<Dialog open={open} onClose={onClose} title="Save Filter Configuration" sm>
			<Form<typeof initialValues>
				initialValues={initialValues}
				validate={validateFormValues(validationSchema)}
				onSubmit={withServerErrors(async ({ name, description }) => {
					await onSave(name, description)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Dialog.Body>
							<Stack gap="4">
								<TextInputField
									name="name"
									type="text"
									label="Name of the Preset"
									placeholder="Enter preset name"
									required
								/>
								<TextAreaInputField
									name="description"
									label="Description of the Preset"
									placeholder="Enter preset description"
									rows={3}
									required
								/>
								<Stack
									gap="1"
									backgroundColor="primary"
									padding="2"
									rounded="md"
								>
									<Text fontWeight="semibold">
										Selected Columns: {selectedCount}
									</Text>
									<Text>Order will be preserved as arranged</Text>
								</Stack>
								<SubmissionError />
							</Stack>
						</Dialog.Body>
						<Dialog.Footer>
							<Inline gap="4" justifyContent="end">
								<Button
									level="tertiary"
									onClick={onClose}
									disabled={submitting}
								>
									Cancel
								</Button>
								<Button level="primary" type="submit" disabled={submitting}>
									<Icons.Ok {...(submitting && { animation: "pulse" })} />
									{submitting ? "Saving..." : "Save"}
								</Button>
							</Inline>
						</Dialog.Footer>
					</form>
				)}
			</Form>
		</Dialog>
	)
}
